import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OMEGA3_HERO_POINTS } from '../data/omega3Data';
import ReviewsOverlay from '../../Reviews';
import Omega3MediaCarousel from './Omega3MediaCarousel';
import useOmega3ReviewStats from '../hooks/useOmega3ReviewStats';

const Omega3Hero = ({ children }) => {
  const { t } = useTranslation();
  const stats = useOmega3ReviewStats();
  const leftContainerRef = useRef(null);
  const rightScrollRef = useRef(null);
  const mainContainerRef = useRef(null);

  // Desktop: while hovering the carousel column, wheel scroll drives the right column first.
  useEffect(() => {
    const leftContainer = leftContainerRef.current;
    const right = rightScrollRef.current;
    if (!leftContainer || !right) return;

    const handleLeftWheel = (e) => {
      if (window.innerWidth < 1024) return;
      if (!leftContainer.contains(e.target)) return;

      const atTop = right.scrollTop <= 0;
      const atBottom = right.scrollTop + right.clientHeight >= right.scrollHeight - 1;
      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;

      if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      right.scrollTop += e.deltaY;
    };

    leftContainer.addEventListener('wheel', handleLeftWheel, { passive: false, capture: true });
    return () => leftContainer.removeEventListener('wheel', handleLeftWheel, { capture: true });
  }, []);

  // Desktop: wheel on left/right margins (outside hero container) should also scroll right panel first.
  useEffect(() => {
    const mainContainer = mainContainerRef.current;
    const right = rightScrollRef.current;
    if (!mainContainer || !right) return;

    const handleOutsideWheel = (e) => {
      if (window.innerWidth < 1024) return;
      if (mainContainer.contains(e.target)) return;

      const rect = mainContainer.getBoundingClientRect();
      const mouseY = e.clientY;
      if (mouseY < rect.top || mouseY > rect.bottom) return;

      const mouseX = e.clientX;
      const isOutsideContainer = mouseX < rect.left || mouseX > rect.right;
      if (!isOutsideContainer) return;

      const atTop = right.scrollTop <= 0;
      const atBottom = right.scrollTop + right.clientHeight >= right.scrollHeight - 1;
      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;

      if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      right.scrollTop += e.deltaY;
    };

    document.addEventListener('wheel', handleOutsideWheel, { passive: false, capture: true });
    return () => document.removeEventListener('wheel', handleOutsideWheel, { capture: true });
  }, []);

  return (
    <section className="t-bg-white t-py-10">
      <div
        ref={mainContainerRef}
        className="container !t-max-w-[1200px] t-grid t-grid-cols-1 lg:t-grid-cols-2 t-items-start t-gap-10 lg:t-gap-0"
      >
        <div ref={leftContainerRef} className="lg:t-sticky lg:t-top-0">
          <Omega3MediaCarousel />
        </div>

        <div
          ref={rightScrollRef}
          className="t-flex t-flex-col t-gap-[18px] lg:t-overflow-y-auto lg:t-max-h-[100vh] hide-scrollbar lg:t-pl-12"
          style={{ overscrollBehavior: 'auto' }}
        >
          <h1
            className="t-text-[32px] md:t-text-[40px] t-leading-[1.2] t-text-navy"
            style={{ fontFamily: 'DM Serif Display, serif' }}
          >
            {t('Omega3_Hero_Title')}
          </h1>
          <ReviewsOverlay
            totalReviews={stats?.count ?? 0}
            averageReviews={parseFloat(stats?.average)?.toFixed(1) ?? 0}
            onClick={() => document.getElementById('omega3-reviews')?.scrollIntoView({ behavior: 'smooth' })}
            score={5}
          />
          <p className="t-leading-[1.7] t-text-sm lg:t-text-base t-text-g500">{t('Omega3_Hero_Subtitle')}</p>
          <div className="t-flex t-flex-col t-gap-3">
            {OMEGA3_HERO_POINTS.map((point) => (
              <div key={point.titleKey} className="t-flex t-items-start t-gap-3">
                <span className="t-text-xl t-leading-none t-mt-[2px] t-flex-none">{point.emoji}</span>
                <p className="!t-m-0 t-text-sm lg:t-text-base t-leading-[1.65] t-text-g500">
                  <strong className="t-text-navy t-font-bold">{t(point.titleKey)}</strong>
                  {' — '}
                  {t(point.bodyKey)}
                </p>
              </div>
            ))}
          </div>
          {children}
        </div>
      </div>
    </section>
  );
};

export default Omega3Hero;
